import * as Joi from "joi";
import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { GenPartial } from 'typeormgen';
import ChangeEpisodicItem from '../change-episodic-items/change-episodic-item.entity';
import ChangeLook from '../change-looks/change-look.entity';
import ChangeScene from '../change-scenes/change-scene.entity';
import Character from '../characters/character.entity';
import CmChangePicture from '../cm-change-pictures/cm-change-picture.entity';
import CmWrapBox from '../cm-wrap-boxes/cm-wrap-box.entity';
import { DEPARTMENTS } from '../enums/enums';
import EpisodicChange from '../episodic-changes/episodic-change.entity';
import EpisodicCharacter from '../episodic-characters/episodic-character.entity';
import Episodic from '../episodics/episodic.entity';
import Production from '../productions/production.entity';

@Entity('changes')
export default class Change extends ChangeLook {
	static readonly tableName = 'changes';
	readonly indexName = 'changes';

	@Column('int')
	episodic_change_id: number;

	@Column('varchar')
	description: string;

	@Column('decimal')
	budget: string;

	@Column('decimal')
	other_costs_total: string;

	@Column('varchar')
	other_costs_description: string;

	@Column('int')
	wrap_box: number;

	@OneToMany(
		(type) => CmChangePicture,
		(cmChangePicture) => cmChangePicture.change
	)
	cmChangePictures: CmChangePicture[];

	@OneToMany((type) => ChangeScene, (changeScene) => changeScene.change)
	changeScenes: ChangeScene[];

	@OneToMany(
		(type) => ChangeEpisodicItem,
		(changeEpisodicItem) => changeEpisodicItem.change
	)
	changeEpisodicItems: ChangeEpisodicItem[];

	@ManyToOne((type) => Character, (character) => character.changes)
	@JoinColumn({
		name: 'char_id',
	})
	character: Character;

	@ManyToOne((type) => Production, (production) => production.changes)
	@JoinColumn({
		name: 'prod_id',
	})
	production: Production;

	@ManyToOne(
		(type) => EpisodicChange,
		(episodicChange) => episodicChange.changes
	)
	@JoinColumn({
		name: 'episodic_change_id',
	})
	episodicChange: EpisodicChange;

	@ManyToOne((type) => CmWrapBox, (cmWrapBox) => cmWrapBox.changes)
	@JoinColumn({
		name: 'wrap_box',
	})
	wrapBox: CmWrapBox;

	scene_ids: number[];

	static rules() {
		return {
			...super.rules(),
			budget: Joi.string().max(11),
			description: Joi.string().allow(null, ''),
			episodic_change_id: Joi.number().integer().allow(null),
			other_costs_description: Joi.string().max(1000).allow(null, ''),
			other_costs_total: Joi.string().max(11).allow(null, ''),
			scene_ids: Joi.array().items(Joi.number().integer()).allow(null),
			wrap_box: Joi.number().integer().allow(null),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			budget: this.budget,
			description: this.description,
			episodic_change_id: this.episodic_change_id,
			other_costs_description: this.other_costs_description,
			other_costs_total: this.other_costs_total,
			scene_ids: this.scene_ids ? this.scene_ids : null,
			wrap_box: this.wrap_box,
		};
	}

	assign(props?: GenPartial<Change>) {
		super.assign(props);

		if (props.scene_ids && typeof props.scene_ids === 'string') {
			// tslint:disable-next-line
			this.scene_ids = (<string>(<any>props.scene_ids))
				.split(',')
				.map((id) => Number(id));
		}
	}

	constructor(props?: GenPartial<Change>) {
		super(props);
	}

	get changeDescription() {
		return this.description;
	}

	set changeDescription(changeDescription: string) {
		this.description = changeDescription;
	}

	getDepartment() {
		return DEPARTMENTS.CM;
	}

	getTagName(data?: {
		production: Production;
		episodic: Episodic;
		characters: Character[];
		episodicCharacter: EpisodicCharacter;
	}) {
		if (!data) {
			return this.change_num;
		}
		const epCharDisplay = data.episodicCharacter.getFullName(data.characters);
		const { is_feature, shoot_method } = data.episodic;
		let episodeNum;
		// if traditional tv, show episode number
		if (!is_feature && !shoot_method && data.production) {
			episodeNum =
				data.production.episode_num && data.production.episode_num !== ''
					? data.production.episode_num
					: 'TBD';
			return `${epCharDisplay} - Change ${this.change_num} - Ep. ${episodeNum}`;
		}
		return `${epCharDisplay} - Change ${this.change_num}`;
	}
}
